import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { AppNotification } from '@iomi/shared';

interface UseNotificationsReturn {
  notifications: AppNotification[];
  loading: boolean;
  error: string | null;
  unreadCount: number;
  hasMore: boolean;
  fetchMore: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  markRead: (notificationId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

interface ListResponse {
  success: boolean;
  data: AppNotification[];
  hasMore: boolean;
  nextCursor?: string;
}

interface UnreadCountResponse {
  success: boolean;
  data: { count: number };
}

const POLL_INTERVAL = 30_000; // 30 seconds

export function useNotifications(orgId: string | null): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const nextCursorRef = useRef<string | undefined>(undefined);

  // Fetch the full notification list (called when the panel/page is opened)
  const fetchNotifications = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<ListResponse>(`/notifications/${orgId}?limit=20`);
      setNotifications(res.data);
      setHasMore(res.hasMore);
      nextCursorRef.current = res.nextCursor;
      // Also update unread count from the list
      setUnreadCount(res.data.filter(n => !n.read).length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // Lightweight unread count fetch (for badge)
  const fetchUnreadCount = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await api.get<UnreadCountResponse>(`/notifications/${orgId}/unread-count`);
      setUnreadCount(res.data.count);
    } catch {
      // Silently ignore polling errors
    }
  }, [orgId]);

  const fetchMore = useCallback(async () => {
    if (!orgId || !hasMore || !nextCursorRef.current) return;
    try {
      const res = await api.get<ListResponse>(
        `/notifications/${orgId}?limit=20&cursor=${nextCursorRef.current}`,
      );
      setNotifications(prev => [...prev, ...res.data]);
      setHasMore(res.hasMore);
      nextCursorRef.current = res.nextCursor;
    } catch {
      // Silently ignore
    }
  }, [orgId, hasMore]);

  const markRead = useCallback(async (notificationId: string) => {
    if (!orgId) return;
    try {
      await api.patch(`/notifications/${orgId}/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true, readAt: new Date().toISOString() } : n),
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silently ignore
    }
  }, [orgId]);

  const markAllRead = useCallback(async () => {
    if (!orgId) return;
    try {
      await api.post(`/notifications/${orgId}/read-all`);
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true, readAt: n.readAt ?? new Date().toISOString() })),
      );
      setUnreadCount(0);
    } catch {
      // Silently ignore
    }
  }, [orgId]);

  // On mount: fetch only the lightweight unread count (badge appears fast)
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Poll unread count every 30s
  useEffect(() => {
    if (!orgId) return;
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [orgId, fetchUnreadCount]);

  return {
    notifications,
    loading,
    error,
    unreadCount,
    hasMore,
    fetchMore,
    fetchNotifications,
    markRead,
    markAllRead,
  };
}
